import { FC } from "react";
import { Doc } from "sharedb/lib/client";

import { getGroupIdFromAccessId, isGroupAccessId } from "../../../../shared/access-id";
import { WorksheetType } from "../../../../shared/types/worksheet";
import { hasStudentTouchedAnyBlocks } from "../../../../shared/worksheets/progress-count";

import { ACCESS_ID_QUERY_PARAM } from "components/constants";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { useAppSelector } from "store/index";
import { pageGroupSelectors, selectSortedUserFullSubmissionsByPageId } from "store/selectors";
import { isIndividualPage } from "utils/page-utils";
import { useQueryParam } from "utils/routing";

interface SubmissionStatusBannerProps {
  page: PageType;
  doc?: Doc<WorksheetType>;
}

export const SubmissionStatusBanner: FC<SubmissionStatusBannerProps> = ({ page, doc }) => {
  const selectedAccessId = useQueryParam(ACCESS_ID_QUERY_PARAM);
  const selectedPageGroup = useAppSelector((s) => {
    if (!isGroupAccessId(selectedAccessId)) return null;
    return pageGroupSelectors.selectById(s, getGroupIdFromAccessId(selectedAccessId));
  });

  const userSubmissions = useAppSelector((s) =>
    selectSortedUserFullSubmissionsByPageId(s, page.id, selectedAccessId || undefined),
  );

  if (!selectedAccessId) {
    return null;
  }

  // Case 1: No submission
  if (userSubmissions.length === 0) {
    const userContext = isIndividualPage(page) ? "student" : "group";

    return (
      <Banner className="page-block-width" kind="warning">
        {t(`warning.no_${userContext}_submission`)}
      </Banner>
    );
  }

  // Case 2: Blank submission for a student or group who hasn't touched any blocks
  if (doc && !hasStudentTouchedAnyBlocks(doc, selectedAccessId)) {
    const isAutoCreatedBlankSubmission = selectedPageGroup?.auto_created;

    return (
      <Banner className="page-block-width" kind={isAutoCreatedBlankSubmission ? "warning" : "info"}>
        {t(
          isAutoCreatedBlankSubmission
            ? "warning.blank_submission_auto_created"
            : "warning.blank_submission",
        )}
      </Banner>
    );
  }

  return null;
};
